import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ArrowLeft, Loader2, Lock } from 'lucide-react'
import { motion } from 'framer-motion'
import FilterBar from '../components/ui/FilterBar'
import LogoItem from '../components/ui/LogoItem'
import { useAuth } from '../context/AuthContext'
import AuthModal from '../components/ui/AuthModal'

export default function CategoryPage() {
  const { slug } = useParams()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
  })
  const [logos, setLogos] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        setShowAuthModal(true)
        setIsLoading(false)
      } else {
        fetchLogos()
      }
    }
  }, [slug, isAuthenticated, authLoading])

  const fetchLogos = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await fetch('/api/cloudinary/logos')
      
      if (!response.ok) {
        throw new Error('Failed to fetch logos')
      }
      
      const data = await response.json()
      
      // Filter logos by category slug
      const categoryMap = {
        'technical': 'Technical Clubs',
        'cultural': 'Cultural Clubs',
        'social': 'Social Clubs',
        'special': 'Special Clubs',
        'institute': 'Institute',
        'departments': 'Departments',
      }
      
      const categoryName = categoryMap[slug]
      const filteredByCategory = data.logos.filter(logo => 
        logo.category === categoryName
      )
      setLogos(filteredByCategory)
    } catch (err) {
      console.error('Error fetching logos:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredLogos = logos.filter((logo) => {
    const matchesSearch = logo.name.toLowerCase().includes(filters.search.toLowerCase())
    return matchesSearch
  })

  const categoryNames = {
    technical: 'Technical Clubs',
    cultural: 'Cultural Clubs',
    social: 'Social Clubs',
    special: 'Special Clubs',
    institute: 'Institute',
    departments: 'Departments',
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-nitrr-background dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-nitrr-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Show auth required if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => window.location.href = '/'}
          defaultMode="signin"
        />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please sign in to view logos in this category and access downloads.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Sign In to Continue
            </button>
          </motion.div>
        </div>
      </>
    )
  }

  return (
  <div className="min-h-screen bg-nitrr-background dark:bg-gray-950">
  <div className="container mx-auto px-4 py-12 max-w-7xl">
  <Link to="/browse" className="inline-flex items-center text-nitrr-primary hover:text-nitrr-primary-dark font-medium transition-colors mb-6">
          <ArrowLeft size={18} className="mr-2" />
          Back to Categories
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-nitrr-text dark:text-white mb-2">
            {categoryNames[slug]}
          </h1>
          <p className="text-nitrr-text-secondary dark:text-gray-300">
            {filteredLogos.length} logos available
          </p>
        </div>

        <FilterBar filters={filters} setFilters={setFilters} />

        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-nitrr-primary" />
            <p className="text-nitrr-text-secondary dark:text-gray-400 mt-4">Loading logos...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 dark:text-red-400">Error: {error}</p>
          </div>
        ) : filteredLogos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-nitrr-text-secondary dark:text-gray-400">
              No logos found in this category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredLogos.map((logo, index) => (
              <LogoItem key={logo._id || logo.id} logo={logo} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
